import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Input,
  OnInit,
  NgZone,
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';

import { ISnPlayer } from '@lib/player/common';
import { COLORS } from '@lib/ui/theme';

@Component({
  selector: 'sn-player-progress',
  templateUrl: './player-progress.component.html',
  styleUrls: ['./player-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlayerProgressComponent implements OnInit, OnDestroy {
  @Input() public player: ISnPlayer;

  private _currentTime = 0;
  private _bufferTime = 0;
  private _totalTime = 0;

  @ViewChild('bufferSlider', { static: true })
  private readonly _bufferSliderElementRef: ElementRef<HTMLElement>;

  @ViewChild('currentSliderInput', { static: true })
  private readonly _currentSliderInputElementRef: ElementRef<HTMLInputElement>;

  private readonly _sliderValueChangeHandlerFn = this._sliderValueChangeHandler.bind(this);
  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _ngZone: NgZone, private readonly _cd: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this._ngZone.runOutsideAngular(() => this._initSubscriptions());
  }

  public ngOnDestroy(): void {
    this._currentSliderInputElementRef.nativeElement.removeEventListener(
      'input',
      this._sliderValueChangeHandlerFn,
    );

    this._destroy$.next();
    this._destroy$.complete();
  }

  private _initSubscriptions(): void {
    this._currentSliderInputElementRef.nativeElement.addEventListener(
      'input',
      this._sliderValueChangeHandlerFn,
    );

    this.player.currentTime$.pipe(takeUntil(this._destroy$)).subscribe((time) => {
      this._currentTime = time;
      this._update();
    });

    this.player.bufferTime$.pipe(takeUntil(this._destroy$)).subscribe((time) => {
      this._bufferTime = time;
      this._update();
    });

    this.player.totalTime$.pipe(takeUntil(this._destroy$)).subscribe((time) => {
      this._totalTime = time;
      this._currentSliderInputElementRef.nativeElement.max = this._totalTime.toString();
      this._update();
    });
  }

  private _update(): void {
    let currentSliderWidth = 0;
    let bufferSliderWidth = 0;

    if (this._totalTime > 0) {
      bufferSliderWidth = (this._bufferTime / this._totalTime) * 100;
      currentSliderWidth = (this._currentTime / this._totalTime) * 100;
    }

    const bufferSliderElement = this._bufferSliderElementRef.nativeElement;
    bufferSliderElement.style.width = `${bufferSliderWidth}%`;

    const currentSliderInputElement = this._currentSliderInputElementRef.nativeElement;

    currentSliderInputElement.value = this._currentTime.toString();
    currentSliderInputElement.style.backgroundImage = `linear-gradient(
      to right,
      ${COLORS['accent-600']} 0%,
      ${COLORS['accent-600']} ${currentSliderWidth}%,
      transparent ${currentSliderWidth}%,
      transparent 100%
    )`;
    this._cd.detectChanges();
  }

  private _sliderValueChangeHandler(): void {
    this._currentTime = +this._currentSliderInputElementRef.nativeElement.value;
    this.player.changeCurrentTime(this._currentTime);
    this._update();
  }
}
